import { Component, HostListener, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '../../../services/notification.service';
import { ConfirmDialogComponent } from '../../../services/confirm-dialog.component';
import {
  ScheduledReport,
  ScheduledReportDraft,
  ScheduledReportFrequency,
} from '../../models/scheduled-report.model';
import { ReportsService } from '../../reports.service';
import { ScheduleReportModalComponent } from '../schedule-report-modal/schedule-report-modal.component';

@Component({
  selector: 'app-scheduled-reports-list',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, ScheduleReportModalComponent],
  templateUrl: './scheduled-reports-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './scheduled-reports-list.component.scss',
})
export class ScheduledReportsListComponent implements OnInit {
  private readonly messageService = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  private readonly reportsService = inject(ReportsService);

  schedules: ScheduledReport[] = [];
  loading = false;
  loadError: string | null = null;
  saving = false;
  openMenuId: string | null = null;
  menuPosition: { top: number; right: number } | null = null;
  modalVisible = false;
  editingSchedule: ScheduledReport | null = null;

  ngOnInit(): void {
    this.loadSchedules();
  }

  loadSchedules(): void {
    this.loading = true;
    this.loadError = null;
    this.reportsService.listScheduledReports().subscribe({
      next: rows => {
        this.schedules = rows;
        this.loading = false;
      },
      error: (err: Error) => {
        this.loading = false;
        this.loadError = err?.message || 'Failed to load scheduled reports';
        this.messageService.add({
          severity: 'error',
          summary: 'Could not load schedules',
          detail: this.loadError,
          life: 4500,
        });
      },
    });
  }

  reportLabel(report: ScheduledReport): string {
    return report.report_type === 'shift' ? 'Shift report' : 'Cash report';
  }

  formatLabel(report: ScheduledReport): string {
    return report.format === 'pdf' ? 'PDF' : 'Excel';
  }

  recipientEmails(report: ScheduledReport): string[] {
    if (Array.isArray(report.recipient_emails) && report.recipient_emails.length) {
      return report.recipient_emails.map(e => String(e).trim()).filter(Boolean);
    }
    return String(report.recipients || '')
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);
  }

  primaryRecipient(report: ScheduledReport): string {
    return this.recipientEmails(report)[0] || '—';
  }

  extraRecipientCount(report: ScheduledReport): number {
    return Math.max(0, this.recipientEmails(report).length - 1);
  }

  /** matTooltip renders plain text, so emails are newline-separated rather than markup. */
  recipientsTooltip(report: ScheduledReport): string {
    return this.recipientEmails(report).join('\n');
  }

  frequencyLabel(frequency: ScheduledReportFrequency, _reportType?: string): string {
    if (frequency.kind === 'shift_end') {
      return 'Automatic (per shift)';
    }
    if (frequency.kind === 'daily') {
      return 'Daily';
    }
    if (frequency.kind === 'weekly') {
      return `Weekly · ${frequency.weekday}`;
    }
    return `Monthly · day ${frequency.day}`;
  }

  toggleMenu(scheduleId: string, event: Event): void {
    event.stopPropagation();
    if (this.openMenuId === scheduleId) {
      this.closeMenu();
      return;
    }
    const trigger = event.currentTarget as HTMLElement | null;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      this.menuPosition = {
        top: rect.bottom + 4,
        right: Math.max(8, window.innerWidth - rect.right),
      };
    } else {
      this.menuPosition = null;
    }
    this.openMenuId = scheduleId;
  }

  closeMenu(): void {
    this.openMenuId = null;
    this.menuPosition = null;
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.openMenuId) {
      this.closeMenu();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.openMenuId) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('[data-schedule-menu]')) {
      return;
    }
    this.closeMenu();
  }

  onNewSchedule(): void {
    this.closeMenu();
    this.editingSchedule = null;
    this.modalVisible = true;
  }

  onEdit(schedule: ScheduledReport): void {
    this.closeMenu();
    this.editingSchedule = schedule;
    this.modalVisible = true;
  }

  onModalCancel(): void {
    if (this.saving) {
      return;
    }
    this.modalVisible = false;
    this.editingSchedule = null;
  }

  onModalSave(event: { draft: ScheduledReportDraft }): void {
    if (this.saving) {
      return;
    }
    const payload = this.reportsService.draftToWritePayload(event.draft);
    const editingId = this.editingSchedule?.id || event.draft.id;
    this.saving = true;

    const request$ = editingId
      ? this.reportsService.updateScheduledReport(editingId, payload)
      : this.reportsService.createScheduledReport(payload);

    request$.subscribe({
      next: row => {
        this.saving = false;
        if (editingId) {
          this.schedules = this.schedules.map(s => (s.id === row.id ? row : s));
          this.messageService.add({
            severity: 'success',
            summary: 'Schedule updated',
            detail: `${this.reportLabel(row)} was updated.`,
            life: 3500,
          });
        } else {
          this.schedules = [row, ...this.schedules];
          this.messageService.add({
            severity: 'success',
            summary: 'Schedule created',
            detail: `${this.reportLabel(row)} was added to Active schedules.`,
            life: 3500,
          });
        }
        this.modalVisible = false;
        this.editingSchedule = null;
      },
      error: (err: Error) => {
        this.saving = false;
        this.messageService.add({
          severity: 'error',
          summary: editingId ? 'Update failed' : 'Create failed',
          detail: err?.message || 'Could not save schedule',
          life: 4500,
        });
      },
    });
  }

  onTogglePause(schedule: ScheduledReport): void {
    this.closeMenu();
    if (this.saving) {
      return;
    }
    const pausing = schedule.status === 'active';
    const label = this.reportLabel(schedule);
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: pausing ? 'Pause schedule' : 'Resume schedule',
          message: pausing
            ? `Pause automatic delivery for this ${label.toLowerCase()}? Recipients will not receive emails until you resume it.`
            : `Resume automatic delivery for this ${label.toLowerCase()}?`,
          accept: pausing ? 'Pause' : 'Resume',
          reject: 'Cancel',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.applyStatusChange(schedule, pausing ? 'paused' : 'active');
        }
      });
  }

  private applyStatusChange(schedule: ScheduledReport, nextStatus: 'active' | 'paused'): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.reportsService.setScheduledReportStatus(schedule.id, nextStatus).subscribe({
      next: row => {
        this.saving = false;
        this.schedules = this.schedules.map(s => (s.id === row.id ? row : s));
        this.messageService.add({
          severity: 'success',
          summary: nextStatus === 'paused' ? 'Schedule paused' : 'Schedule resumed',
          detail: `${this.reportLabel(row)} is now ${nextStatus}.`,
          life: 3500,
        });
      },
      error: (err: Error) => {
        this.saving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Status update failed',
          detail: err?.message || 'Could not update schedule status',
          life: 4500,
        });
      },
    });
  }

  onDelete(schedule: ScheduledReport): void {
    this.closeMenu();
    if (this.saving) {
      return;
    }
    const label = this.reportLabel(schedule);
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Delete schedule',
          message: `Delete this ${label.toLowerCase()} schedule? This cannot be undone and future automatic emails will stop.`,
          accept: 'Delete',
          reject: 'Cancel',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.applyDelete(schedule);
        }
      });
  }

  private applyDelete(schedule: ScheduledReport): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.reportsService.deleteScheduledReport(schedule.id).subscribe({
      next: () => {
        this.saving = false;
        this.schedules = this.schedules.filter(row => row.id !== schedule.id);
        this.messageService.add({
          severity: 'success',
          summary: 'Schedule removed',
          detail: `${this.reportLabel(schedule)} was deleted.`,
          life: 3500,
        });
      },
      error: (err: Error) => {
        this.saving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Delete failed',
          detail: err?.message || 'Could not delete schedule',
          life: 4500,
        });
      },
    });
  }

  trackByScheduleId(_index: number, schedule: ScheduledReport): string {
    return schedule.id;
  }
}
